/** Every user-visible string of the Meetings area (decision 176: "Meetings", never "Conversations"). */
export const COPY = {
  area: "Meetings",
  description:
    "Recordings, transcripts, and scorecards from every meeting in this project.",
  docs: "Docs",
  docsUrl: "https://docs.neuroscale.ai/core/meetings",
  breadcrumb: (title: string) => ["Meetings", title] as const,
  tabs: { all: "All", schedule: "Schedule", attention: "Needs attention", generators: "Generators" },
  empty: {
    all: "No meetings yet. Upload a recording to get started.",
    schedule: "No scheduled meetings",
    attention: "Nothing needs attention.",
    generators: "Generators arrive with document generation.",
    job: "No meetings for this job yet.",
    candidate: "No meetings for this candidate yet.",
    noProject: "Select a project to see its meetings.",
  },
  errors: {
    loadFailed: "Couldn't load meetings.",
    columnsStale: "Meeting settings changed. Reload to continue.",
    classLoweringAdmin: "An org admin has to lower this meeting's class.",
    classLoweringConsent: "Can't lower the class while a consent hold is in place.",
    invalidMeetingType: "That meeting type isn't valid.",
    relinkTargetForbidden: (side: string) =>
      side === "source"
        ? "You need edit access on the current job"
        : "You need edit access on the target job",
    jobNotFound: "That job wasn't found.",
    grantEntityForbidden: "You also need manage-access on that job's project.",
    userNotFound: "That person is not in your organization.",
    conversationUnresolved: "Bind the meeting to a job first.",
    propertyTypeMismatch: "That value doesn't match the property type.",
    propertyNotFound: "That property isn't on this meeting.",
    conversationNotFound: "That meeting doesn't exist or you don't have access.",
    retentionPreviewRequired: "Review the retention change before saving.",
    retentionPreviewStale: "That preview expired. Review the change again.",
    later: "later",
    rateLimited: (t: string) => `Too many requests. Try again after ${t}.`,
    uploadPresignDaily: (t: string) => `Daily upload limit reached. Try again after ${t}.`,
    uploadBytesCap: (t: string) => `Your organization's daily upload volume is used up. Try again after ${t}.`,
    asrMinutesCap: (t: string) => `Your organization's daily transcription minutes are used up. Try again after ${t}.`,
    purgeRateLimited: (t: string) => `Too many deletions this hour. Try again after ${t}.`,
    uploadTokenInvalid: "This upload expired. Start it again.",
    channelDisabled: "Uploads are turned off for your organization.",
    featureNotInPlan: "Meeting capture is not on your plan.",
    sourceNotFound: "That source no longer exists.",
    speakerLabelNotFound: "That speaker label no longer exists. Reload the meeting.",
    participantNotFound: "That participant no longer exists. Reload the meeting.",
    documentNotFound: "No document found for that selection — the candidate may have no resume on file.",
    projectNotFound: "That project wasn't found.",
    projectAccessDenied: "You don't have access to that project.",
    candidateNotInJob: "That candidate isn't on the selected job.",
    candidateDeclined: "This candidate has declined to be recorded.",
    legalHoldActive: (until: string) => `This meeting is under legal hold until ${until}.`,
    transcriptCapReached: "This recording has reached its transcription limit.",
    participantAlreadyClaimed: "Someone else has already claimed this participant.",
    cannotSelfCandidate: "You can't mark yourself as the candidate — you'd lose access to this meeting.",
    conversationProcessing: "This meeting is still processing. Try again when it's ready.",
    conversationPurged: "This meeting's content was removed; it can't be edited.",
    sourceNotDetachable: "Only notes and attached documents can be detached.",
    uploadAlreadyFinalized: "This upload was already finished.",
    uploadTooLarge: "That file is larger than 4 GB.",
    uploadSizeMismatch: "The uploaded size didn't match. Start the upload again.",
    importTooLarge: "That transcript is too large (5 MB limit).",
    uploadContentType: "Use a .wav, .mp3, .mp4, .mpeg, .mov, .webm, .m4a or .ogg file.",
    uploadTooShort: "Recordings must be at least 60 seconds long.",
    uploadTooLong: "Recordings can be at most 5 hours long.",
    importSpeakerUnknown: "Every line's speaker must be one of the participants.",
    attestationRequired: "Confirm the recording was made with the participants' consent.",
    invalidLanguage: "That language isn't supported.",
    processingUnavailable: "Processing is temporarily unavailable. Try again in a moment.",
    mediaNotAvailable: "The recording is no longer available.",
    uploadUnsupportedType: "Use a .wav, .mp3, .mp4, .mpeg, .mov, .webm, .m4a or .ogg file.",
    uploadDurationUnreadable: "Couldn't read the recording's length. Is the file a valid recording?",
    uploadEtagMissing: "The storage service didn't confirm a part. Ask an admin to check the bucket's CORS settings.",
    uploadPartFailed: "A part of the upload failed after several attempts. Check your connection and try again.",
    uploadCancelled: "Upload cancelled.",
    recorderUnsupported: "This browser can't record audio. Use a recent Chrome, Edge, Firefox or Safari.",
    recorderTabUnsupported: "This browser can't record another tab's audio. Record the microphone only, or use Chrome or Edge on a computer.",
    recorderPermissionDenied: "Microphone access is blocked. Allow it in your browser's site settings, then try again.",
    recorderNoDevice: "No microphone found. Connect one, then try again.",
    recorderDeviceBusy: "The microphone couldn't be started. Close other apps using it, then try again.",
    recorderShareCancelled: "Sharing was cancelled, so nothing is being recorded.",
    recorderNoTabAudio: "The shared tab or window has no audio. Share again and turn on “Share tab audio”, or record the microphone only.",
    recorderFailed: "Recording stopped unexpectedly. Try again.",
    claimRoleConflict: "You can only claim a participant whose role is Interviewer.",
    adhocPoolBusy: (s: number) => `Every meeting bot is busy right now. Try again in ${s} seconds.`,   // default 30 = 04b ADHOC_RETRY_AFTER_S
    orgBotLimit: (n: number) => `Your organization already has ${n} live meeting bots. Wait for one to finish.`,
    meetingUrlInvalid: "That doesn't look like a meeting link.",
    platformUnsupported: "Only Zoom, Google Meet, Microsoft Teams and Webex links are supported.",
    captureDisabledForUser: "Meeting capture is turned off for you. Turn it on under My meetings.",   // the route is CONVERSATIONS_ROUTES.personalSettings (/settings/my-conversations) — its page title is COPY.settings.personal; keep this wording in step with it when 03d's copy lands
    captureNotLive: "The bot isn't in this meeting — it hasn't joined yet, or it has already left.",
    meetingAlreadyCaptured: "Someone in your organization is already recording this meeting. Ask them to share it with you.",
    triageNotMultiCandidate: "This meeting isn't flagged as a group interview.",
    calendarNotOwned: "Only the calendar's owner or an org admin can change it.",
    calendarConnectionNotFound: "That calendar connection no longer exists.",
    calendarEventNotFound: "That meeting is no longer on the calendar.",
    calendarAlreadyConnected: "That calendar is already connected.",
    calendarDeleted: "This calendar was disconnected. Connect it again instead of reconnecting.",
    oauthStateInvalid: "The sign-in expired. Start again.",
    oauthProviderError: "The calendar provider refused the sign-in. Try again.",
    maxCalendarsReached: (n: number) => `Your organization has reached its calendar limit of ${n}.`,
    scheduleOverrideForbidden: "Only an attendee or an org admin can change this meeting's bot.",
    scheduleOverrideTooLate: "This meeting can no longer be overridden — it already started, ended, or the bot could not join.",
    recallUnavailable: "The meeting-bot service is unavailable. Try again in a moment.",
    consentOverrideForbidden: "Only an org admin or this meeting's interviewer can record consent for someone else.",
    consentStandingDecline: "This participant has declined recording. A choice on their behalf can't override that.",
    consentTokenInvalid: "That consent link isn't valid.",
    consentTokenExpired: "That consent link has expired.",
    consentTokenUsed: "That consent link was already used.",
    consentChoiceMismatch: "That link records a different choice.",
    cutDeferred: "A removal is waiting on this meeting. Processing continues once it has run.",
    cutInProgress: "A removal is already running on this meeting.",
    replyNotFound: "That reply no longer exists. Reload the meeting.",
    replyAlreadyHandled: "That reply was already handled.",
    replyNotAuthenticated: "This reply couldn't be verified, so consent can't be recorded from it. Send a fresh link instead.",
    participantHasNoAddress: "This participant has no email address on file, so a notice can't be sent.",
    participantRequired: "Pick which participant this reply is from before recording a choice.",
    noticeTemplateIncomplete: (slot: string) => slot === "org_paragraph" ? "Add your organization's disclosure paragraph in Settings before sending a notice." : `The notice template is missing its “${slot}” part.`,
    noticeRateLimited: (t: string) => `Too many notices to this participant today. Try again after ${t}.`,
    erasureRequestNotFound: "That erasure request no longer exists.",
    erasureSubjectInvalid: "Pick a profile or enter an email address — one of the two.",
    profileNotFound: "That profile isn't in your organization.",
    dsarTooLarge: (n: number | null) => n === null ? "Too many meetings for one bundle. Narrow the subject to an email address." : `Too many meetings for one bundle (${n}). Narrow the subject to an email address.`,
    legalHoldInvalid: "A legal hold has to end in the future, or be cleared.",
    // ── spec 06 §3.5 verified domains ──
    verifiedDomainNotFound: "That domain is no longer on the list.",
    verifiedDomainExists: "That domain is already on the list.",
    verifiedDomainLimit: (max: number | null) =>
      max === null
        ? "You have reached the limit on verified domains. Remove one before adding another."
        : `You can verify up to ${max} domains. Remove one before adding another.`,
    verifiedDomainInvalid: "That does not look like a domain. Enter something like acme.com — no @, no spaces and no wildcards.",
    verifiedDomainPublic: "That is a public email provider, so it cannot tell your colleagues apart from anyone else. Add a domain your organization owns.",
    summaryNotReady: "The summary isn't ready to download yet.",
    summaryPdfUnavailable: "Couldn't build the PDF right now. Try again in a moment.",
    summaryPdfFailed: "Couldn't build the PDF for this summary.",
    matchAlreadyResolved: "This meeting is already matched. Change its job or candidate instead.",
  },
  actions: {
    retry: "Retry",
    reload: "Reload",
    backToMeetings: "Back to meetings",
  },
  untitled: "Untitled meeting",
  unmatched: "Unmatched",
  /** The binding label of an unmatched meeting a person marked as internal or personal. */
  matchNotNeeded: "No match needed",
  /** Group-button labels on Needs attention. Row-cell copy stays in ATTENTION_LABELS. */
  attentionGroups: {
    unresolved: "Unmatched — no job",
    job_no_candidate: "Unmatched — no candidate",
  },
  columns: {
    actions: "Actions",
    title: "Title",
    meeting_type: "Type",
    state: "State",
    entity: "Job / candidate",
    participants: "Participants",
    scheduled_start: "When",
    attention: "Needs attention",
  },
  filters: {
    add: "Add filter",
    remove: "Remove filter",
    removeNamed: (label: string) => `Remove ${label} filter`,
    from: "From",
    to: "To",
    value: "Value",
    on: "On",
    off: "Off",
  },
  aria: {
    tabs: "Meetings sections",
    attentionGroup: "Attention group",
  },
  rowActions: {
    label: (title: string) => `Actions for ${title}`,
    open: "Open meeting",
    openInNewTab: "Open in new tab",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    noMatchDone: "Marked as no match needed",
    needsMatchDone: "Marked as needing a match",
    archived: "Meeting archived",
    unarchived: "Meeting restored",
  },
  header: {
    share: "Share", archive: "Archive", unarchive: "Unarchive", relink: "Change job / candidate",
    relinkAfterReady: "Available with document generation (spec 07)",
    noEdit: "You need edit access on this meeting's job",
    noManage: "You need manage-access on this meeting's project",
    details: "Details",
    rename: "Rename",
    joinMeeting: "Join meeting",
    partial: "Partial",
    multiCandidate: "Multiple candidates",
    markNoMatch: "Mark as no match needed",
    markNeedsMatch: "Needs a match",
    archived: "Archived",
    failure: {
      // The accessible label on `FailureNote`'s wrapper, so the reason, the partial reason and
      // the guidance read as one named group rather than three stray paragraphs.
      why: "Why this failed",
    },
  },
  aside: {
    heading: "Details",
    sources: "Sources", access: "Access", consent: "Consent", documents: "Documents", notes: "Notes", speakers: "Speakers",
    documentsSoon: "Documents arrive with generation.",
    revoke: "Revoke",
    revoked: "Revoked",
    media: "Has media",
    transcript: "Has transcript",
    quality: "Quality",
    duration: "Duration",
    consentGranted: "Granted",
    consentDeclined: "Declined",
    consentWithdrawn: "Withdrawn",
    consentPending: "Pending",
    consentNotRequired: "Not required",
    consentNotCollected: "Not collected",
    summary: "Summary",
    summarizing: "Summarizing…",
    summaryFailed: "Couldn't summarize this call.",
    summaryRetry: "Try again",
    summaryView: "View summary",
    summaryDownload: "Download PDF",
    summaryError: {
      unauthorized: "The summary model rejected the gateway key. Check the model, then try again.",
      invalid_output: "The summary came back in the wrong shape.",
      model_rejected: "The summary model rejected the request.",
      unfinished: "The summary didn't finish.",
    },
  },
  media: {
    documentOnly: "You have document access only.",
    purged: (date: string) => `Content was removed on ${date}; metadata is kept.`,
    removed: "removed",
    skipBack: "Skipped back 5 seconds",
    skipForward: "Skipped forward 5 seconds",
    markers: "Markers",
    // The tail past a cut on the marker track. Never announced — the track is
    // aria-hidden (§5) — and deliberately says what the region is not rather
    // than that something was removed from it.
    notRecorded: "Not part of the recording",
    transcript: "Transcript",
    sections: "Recording",
    meeting: "Meeting",
    overview: "Overview",
    language: "Language",
    when: "When",
    participants: "Participants",
    noParticipants: "No participants yet.",
    linked: "Linked",
    loadMore: "Load more",
    playbackFailed: "Couldn't play this recording.",
    preparing: "Preparing transcript…",
    empty: "No transcript yet.",
    /** What the page says in place of a player and a transcript skeleton. */
    captureStatus: {
      scheduled: (when: string) =>
        `The bot joins ${when}. Nothing is recorded until the meeting starts.`,
      scheduledOpen: "The bot has not joined yet. Nothing is recorded until the meeting starts.",
      joining: "Arbi is joining the meeting. Nothing is being recorded yet.",
      await_member: "Waiting for someone from your organization to be in the meeting. Nothing is being recorded.",
      await_consent: "Waiting for consent. Recording has not started.",
      capturing: "This meeting is being recorded. The transcript shows up here when processing finishes.",
      paused: "Recording is paused. A member can resume it from the call.",
      consent_hold: "Recording is on hold until consent is settled.",
      member_hold: "Recording is on hold until a member is in the meeting.",
      processing: "Turning this recording into a transcript.",
      skipped: "This meeting was not recorded.",
      declined: "Recording was declined.",
      blocked: "A bot was not sent to this meeting.",
      cancelled: "This meeting was cancelled before a recording was kept.",
      failed: "This meeting could not be recorded.",
      empty: "The meeting ended without anything to keep.",
      discarded: "The recording was discarded.",
      no_show: "Nobody joined, so nothing was recorded.",
      revoked: "The recording was removed.",
      consent_withdrawn: "Consent was withdrawn, so the recording was not kept.",
      unknown: "There's no recording for this meeting.",
    },
  },
  /**
   * The speaker timeline panel. The strips are `aria-hidden`, so every string
   * here that is announced belongs to a real button: the rows, the per-row
   * play buttons, the focus pill and the header transport.
   */
  timeline: {
    heading: "Timeline",
    hide: "Hide timeline",
    show: "Show timeline",
    play: "Play",
    pause: "Pause",
    restart: "Restart",
    // The lanes are not drawn until every segment page is in, so this is a
    // real state and not a flash — see `useAllSegments`.
    loading: "Loading the whole transcript…",
    empty: "No speech to lay out yet.",
    // `duration_ms`, the element's metadata and the source's duration are all
    // unknown. There is no honest extent to draw against.
    noExtent: "This recording's length isn't known yet.",
    speakers: "Speakers",
    screenShare: "Screen share",
    events: "Events",
    shortcuts: "Space play/pause · J / L skip 10s",
    focus: (name: string) => `Focus ${name}`,
    focusing: (name: string) => `Focusing ${name}`,
    stopFocus: (name: string) => `Stop focusing ${name}`,
    playOnly: (name: string) => `Play only ${name}`,
    focusEnded: (name: string) => `${name} has no more turns. Focus off.`,
    talkTime: (pct: number, duration: string) =>
      `${pct}% of speech, ${duration}`,
    /** Announced under the lanes; names the denominator the percentages use. */
    denominator: "Percentages are each speaker's share of all speech.",
  },
  /** The Metrics section of the Overview tab (MET-1, MET-1a, MET-2, MET-6). */
  metrics: {
    heading: "Metrics",
    pending: "Metrics appear once the recording ends and is processed.",
    none: "No metrics for this meeting.",
    timing: "Timing",
    expected: "Scheduled length",
    actual: "Recorded length",
    lateStart: "Start",
    overrun: "End",
    botJoin: "Bot joined",
    capture: "Capture",
    attendance: "Participants",
    silence: "Silence",
    gaps: "Gaps",
    onTime: "On time",
    late: (min: number) => `${min} min late`,
    early: (min: number) => `${min} min early`,
    over: (min: number) => `${min} min over`,
    endedEarly: (min: number) => `${min} min early`,
    botAtStart: "At start",
    botAfter: (d: string) => `${d} after start`,
    botBefore: (d: string) => `${d} before start`,
    withAudio: (n: number) => `${n} with audio`,
    invitedAbsent: (n: number) => `${n} invited, absent`,
    silenceLongest: (total: string, longest: string) => `${total} total · longest ${longest}`,
    noGaps: "None",
    gapSummary: (n: number, total: string) => `${n} gap${n === 1 ? "" : "s"} · ${total}`,
    sessions: (n: number) => `${n} session${n === 1 ? "" : "s"}`,
    away: (d: string) => `${d} away`,
    speech: "Speech",
    interviewers: "Interviewers",
    candidates: "Candidates",
    shareBar: (interviewer: number, candidate: number) =>
      `Interviewers ${interviewer}% of speech, candidates ${candidate}%`,
    columns: {
      name: "Name",
      role: "Role",
      joined: "Joined",
      presence: "Presence",
      talkTime: "Talk time",
      share: "Share",
      wpm: "WPM",
      longest: "Longest",
      interruptions: "Interruptions",
    },
    interruptionsTitle: "Interruptions made / received",
    interruptionsUnavailable:
      "Interruptions need separate audio per person, which this transcript doesn't have.",
    interruptedDetail: (name: string, n: number) => `${name} ${n}×`,
    withheld: "Hidden by your organization's metrics setting",
    unnamed: "Unnamed participant",
    unknown: "—",
    /** One line per closed reason code the API sends in `unavailable` / `unavailable_reason`. */
    reasons: {
      no_schedule: "No scheduled time for this meeting",
      no_transcript: "No transcript available",
      no_bot: "No bot joined this meeting",
      no_sessions: "No participant audio was recorded",
      diarization_machine:
        "Speakers were told apart automatically, so per-person speech metrics aren't reliable.",
      diarization_none: "Speakers weren't separated in this recording.",
      no_media_access: "Speech metrics need recording access.",
    },
    reasonFallback: "Not available",
  },
  properties: {
    heading: "Properties",
    scorecardType: "Scorecard type",
    unselected: "Pick a scorecard type",
    title: "Title",
    type: "Meeting type",
    when: "When",
    binding: "Binding",
    save: "Save",
    boundTo: (level: string) => `Bound to ${level}`,
    add: "Add property",
    addName: "Property name",
    addType: "Property type",
    addValue: "Value",
    addCustom: "New on this meeting",
    empty: "No properties on this meeting yet.",
    createHint: "New fields stay on this meeting. Org-wide fields live in Settings.",
    askAdmin: "Ask an org admin to add a custom property.",
  },
  relink: {
    title: "Change job / candidate",
    job: "Job",
    candidate: "Candidate (optional)",
    submit: "Relink",
    jobOnly: "Keep job only",
    selectJob: "Select a job",
    selectCandidate: "Select a candidate",
    noJobs: "No jobs in this project.",
  },
  access: {
    empty: "No one has been granted access yet.",
    revoked: "Revoked",
  },
  capture: {
    title: "Add a meeting",
    open: "Upload",
    join: "Join",
    record: "Capture",
    tabs: { record: "Record", upload: "Upload", import: "Import transcript", pasteLink: "Paste a link" },
    drop: "Drop a recording here",
    recordedOnRequired: "Enter when the recording was made.",
    browse: "Choose a file",
    limits: ".wav .mp3 .mp4 .mpeg .mov .webm .m4a .ogg · up to 4 GB · 1 min to 5 h",
    recorder: {
      source: "Record from",
      microphoneLabel: "Microphone",
      defaultMicrophone: "System default",
      microphoneN: (n: number) => `Microphone ${n}`,
      chooseMicrophone: "Choose a different microphone",
      mic: "Microphone",
      micDescription: "Your voice and anything your microphone picks up.",
      micAndTab: "Microphone + a tab or window",
      micAndTabDescription: "For a call in another tab or app: its audio is recorded together with your microphone.",
      shareHint: "When your browser asks what to share, pick the call's tab and turn on “Share tab audio”.",
      consent: "Everyone on this call knows it's being recorded.",
      start: "Start recording",
      starting: "Waiting for permission…",
      pause: "Pause",
      resume: "Resume",
      stop: "Stop and upload",
      recording: "Recording",
      paused: "Paused",
      level: "Input level",
      tooShortTitle: "Record at least a minute",
      tooShortBody: "Recordings shorter than a minute can't be processed.",
      keepRecording: "Keep recording",
      discard: "Discard",
      maxReached: "Recording stopped just before the 5-hour limit.",
      recorded: (duration: string, size: string) => `Recording · ${duration} · ${size}`,
    },
    recordedOn: "Recorded on",
    titleField: "Title (optional)",
    meetingType: "Meeting type",
    hint: "Job or candidate",
    hintPlaceholder: "Optional",
    hintPickerTitle: "Link to a job or candidate",
    hintClear: "Remove link",
    hintSubmit: "Link",
    noProject: "Select a project, or pick a job or candidate.",
    start: "Start upload",
    cancel: "Cancel upload",
    phases: {
      hashing: "Checking file",
      probing: "Reading length",
      presigning: "Preparing upload",
      uploading: "Uploading",
      completing: "Finishing",
      done: "Uploaded — processing has started",
      aborted: "Cancelled",
      failed: "Upload failed",
    },
    parts: (done: number, total: number) => `${done} / ${total} parts`,
    selected: (name: string, size: string) => `${name} · ${size}`,
    importPaste: "Paste a transcript",
    importPastePlaceholder: "Sam Lee: Thanks for joining…\nJane Doe: Happy to be here.",
    importFile: "or choose a .vtt, .srt or .txt file",
    importPreview: "Speakers",
    importSegments: (n: number) => `${n} line${n === 1 ? "" : "s"}`,
    importRole: "Role",
    importNoRole: "No role",
    importMe: "This is me",
    importMeNone: "I'm not in this transcript",
    importWarnings: (n: number) => `${n} line${n === 1 ? "" : "s"} skipped`,
    attest: "I confirm this recording was made with the participants' consent.",
    importSubmit: "Import",
    importEmpty: "Nothing to import yet — paste a transcript or choose a file.",
    importTooManySegments: "That transcript has more than 20,000 lines.",
    importTooManyParticipants: "That transcript has more than 50 speakers.",
    importTooLarge: "That transcript is too large (5 MB limit).",
    pasteLink: {
      url: "Meeting link",
      urlPlaceholder: "https://zoom.us/j/…",
      urlInvalid: "Enter a Zoom, Google Meet, Microsoft Teams or Webex link.",
      join: "Join now",
      sent: "The bot is on its way.",
    },
    adhocWaiting: "Waiting for a free bot… this can take up to 30 seconds.",
    controls: "Recording controls",
    stopLeave: {
      stop: "Stop recording",
      leave: "Leave meeting",
      stopTitle: "Stop recording?",
      leaveTitle: "Leave the meeting?",
      stopBody: "The bot stops recording and leaves the call. If the capture meets your organization's minimum length it is processed as this meeting; a shorter capture is discarded.",
      leaveBody: "The bot leaves the call now. If the capture meets your organization's minimum length it is processed; a shorter capture is discarded.",
      preRecordingBody: "Nothing has been recorded yet. The bot is cancelled and this meeting is marked Cancelled.",   // joining | await_* → 04b cancelled(admin), §10.51
      confirmStop: "Stop",
      confirmLeave: "Leave",
      sending: "Sending",
      sent: "Leave requested. The bot leaves once the command is delivered.",
      sentCancel: "Cancel requested. The bot is cancelled once the command is delivered.",
    },
    holdResume: {
      hold: "Hold recording",
      resume: "Resume",
      resumeAnyway: "Resume anyway",
      held: "Recording is on hold.",
      resumed: "Recording resumed.",
    },
  },
  consent: {
    heading: "Consent",
    mode: { opt_out: "Opt out — participants are recorded unless they decline", opt_in: "Opt in — recording waits for each participant's consent" },
    policySource: (source: string) => `Policy: ${source}`,
    policySources: { org_default: "organization default" },
    legalHold: {
      action: "Legal hold…",
      title: "Legal hold",
      body: "While a hold is set, nothing on this meeting is removed — not by retention, a withdrawal, a revocation or an erasure. Removals queue and run when the hold is lifted.",
      until: "Hold until",
      active: (date: string) => `Legal hold until ${date}`,
      clear: "Clear hold",
      save: "Set hold",
      saving: "Saving",
      set: "Legal hold set.",
      cleared: "Legal hold cleared.",
    },
    revoke: {
      action: "Revoke recording…",
      title: "Revoke this recording?",
      body: "Recording stops if it is still running. Everything from the chosen participant's first join to the end is removed from the recording and transcript. Without a participant, the removal starts where regulated speech began.",
      participant: "Participant",
      unidentified: "Not identified",
      confirm: "Revoke",
      revoking: "Revoking",
    },
    withdraw: {
      action: "Withdraw",
      title: "Withdraw your consent?",
      body: "Your speech from when you joined to the end of the meeting is removed from the recording and transcript. Anything recorded before you joined stays.",
      bodyAdmin: "This participant's speech from when they joined to the end of the meeting is removed from the recording and transcript. Anything recorded before they joined stays.",
      confirm: "Withdraw consent",
      withdrawing: "Withdrawing",
    },
    onBehalf: { grant: "Record on behalf", decline: "Mark declined", granted: "Consent recorded on their behalf.", declined: "Decline recorded on their behalf." },
    resend: { action: "Resend notice", sent: "A fresh notice is on its way." },
    cut: {
      live: "Recording stops now. The removal runs when processing finishes.",
      processing: "Queued — the removal runs when processing finishes.",
      cutRunning: "Queued behind a removal that is already running.",
      legalHold: "Recorded. The removal waits for the legal hold to be lifted.",
      started: "Recorded. The removal has started.",
      recorded: "Recorded.",
    },
    flags: { revoked: (date: string) => `Revoked ${date}`, cutMediaLost: "The recording could not be trimmed and was removed entirely.", cutQueued: "A removal is queued." },
    tooltip: (rank: string, source: string, when: string) => `${rank} · ${source} · ${when}`,
    never: "—",
    replies: {
      heading: "Replies",
      none: "No replies to the notice yet.",
      verified: "Verified",
      notVerified: "Not verified",
      auth: (spf: string, dkim: string, dmarc: string) => `SPF ${spf} · DKIM ${dkim} · DMARC ${dmarc}`,
      unknown: "unknown",
      noSubject: "(no subject)",
      forParticipant: "For participant",
      consent: "Record consent on their behalf",
      decline: "Record decline on their behalf",
      dismiss: "Dismiss",
      freshLink: "Send a fresh link",
      handled: (action: string, when: string) => `${action} · ${when}`,
      handledActions: { consent: "Consent recorded", decline: "Decline recorded", withdraw: "Withdrawn", dismiss: "Dismissed" },
      done: "Reply handled.",
    },
  },
  triage: {
    mediaOnly: "Speaker assignment needs recording access.",
    processing: "Speakers can be assigned once processing finishes.",
    // Speakers come from the transcript, which is written after the call — nothing to show or block while live.
    live: "Speakers appear once the recording ends and is processed.",
    unassigned: (n: number) => `${n} speaker${n === 1 ? "" : "s"} unassigned`,
    unassignedOption: "Not assigned",
    allAssigned: "All speakers assigned.",
    talkTime: "Talk time",
    samples: "Sample lines",
    assignTo: "Assign to",
    newPerson: "New person…",
    newPersonName: "Name",
    newPersonSave: "Save name",
    newPersonNeedsAssignment: "Assign this speaker to a person first.",
    role: "Role",
    applyRole: "Apply role",
    cancelRole: "Cancel",
    me: "This is me",
    previewLosesAccess: "You'll lose access to this meeting if you apply this role.",
    none: "No speech was transcribed in this recording.",
    never: "—",
    multiCandidate: {
      heading: "Group interview",
      intro: "Several candidates were in this meeting. Assign the whole meeting to one candidate, or generate a separate thread per candidate.",
      single: "Candidate",
      candidateFor: "Candidate for",
      noCandidate: "Not a candidate",
      unnamed: "Unnamed candidate",
      assignOne: "Assign to one",
      perCandidate: "Generate per candidate",
      needsJob: "Link this meeting to a job before triaging candidates.",
      truncated: (shown: number, total: number) =>
        `Showing ${shown} of ${total} candidates — the rest are not listed.`,
      done: (n: number) => `${n} thread${n === 1 ? "" : "s"} updated`,
    },
  },
  notes: {
    placeholder: "Notes about this meeting…",
    save: "Save notes",
    saved: (who: string | null, when: string) => `Saved by ${who ?? "a teammate"} · ${when}`,
    unsaved: "Unsaved changes",
    tooLong: "Notes are limited to 50 KB.",
    readOnly: "Content was removed from this meeting; notes are read-only.",
    empty: "No notes yet.",
    loading: "Loading notes…",
  },
  sources: {
    none: "None",
    attach: "Attach document",
    attachTitle: "Attach a document",
    attachHint: "Pick a job to attach its description, or a candidate to attach their resume.",
    attachWillUseJob: "Attaches the job description.",
    attachWillUseResume: "Attaches the candidate's resume on file.",
    attachSubmit: "Attach",
    detach: "Detach",
    // Rendered only above one attempt: every transcript has at least one, so
    // "1 transcription attempt" was a chip that could never tell anyone
    // anything. Above one it means a retry happened, which is worth a chip.
    attempts: (n: number) => `${n} transcription attempts`,
    diarization: (quality: string) => `${quality} diarization`,
    attested: (when: string) => `Consent attested ${when}`,
    uploadedBy: (who: string | null) => `Uploaded by ${who ?? "a teammate"}`,
    purged: "Content removed",
    purgedHint: "Content was removed from this meeting; documents can't be attached.",
    play: (name: string) => `Play ${name}`,
    playing: "In player",
    /**
     * Chat and screen share are not recordings. `build_side_sources` pairs the
     * platform's `screenshare_on` / `screenshare_off` events into windows and
     * parks the JSON in `s3_key_transcript`, the only text column on a Source —
     * which is why they used to render as cards claiming "Has transcript" next
     * to "Quality: None · Duration: None". They have no media and can never be
     * played; the timeline draws the windows properly.
     */
    alsoCaptured: (kinds: string) => `Also captured: ${kinds} — shown on the timeline.`,
  },
  language: {
    label: "Language",
    stored: "Language saved",
    notTranscribable: "Saved. This meeting has no recording to re-transcribe.",
    featureNotInPlan: "Saved. Re-transcription is not on your plan.",
    retranscribing: (n: number) => `Re-transcribing · ${n} run${n === 1 ? "" : "s"} left (each run may use up to two attempts)`,
  },
  deleteMeeting: {
    more: "More",
    action: "Delete meeting",
    title: "Delete this meeting's content",
    removes: "This permanently removes the recording, the transcript text, and attached documents. Notes stay on the conversation.",
    keeps: "Kept: participants and their consent, notices and join decisions, consent records, capture events, usage meters, and the audit trail.",
    confirm: "Delete content",
    deleting: "Deleting",
    done: "Content removed",
  },
  live: (title: string, state: string) => `${title} is now ${state}`,
  badge: (n: number) => `${n} item${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} attention`,
  settings: {
    org: "Notetaker",
    personal: "My meetings",
    orgDescription: "Organization defaults for meeting capture, retention, and sharing.",
    personalDescription: "Your capture and notification preferences for meetings.",
    askAdmin: "Ask an org admin to change these settings.",
    advanced: "Advanced",
    sections: {
      botDesign: "Bot design",
      consent: "Consent",
      retention: "Retention & media",
      channels: "Capture channels",
      verifiedDomains: "Verified domains",
      bot: "Bot behavior",
      visibility: "Visibility & sharing",
      metrics: "Metrics",
      language: "Language",
      data: "Data",
      output: "Output",
      limits: "Limits",
      erasure: "Erasure & DSAR",
    },
    laterPhase1: "Later in Phase 1",
    setByArbi: "Set by Arbi",
    retentionPreviewTitle: "Review the retention change",
    save: "Save",
    discard: "Discard",
    saveProperties: "Save properties",
    confirm: "Confirm",
    cancel: "Cancel",
    addProperty: "Add property",
    archive: "Archive",
    newProperty: "New property",
    noneMapping: "—",
    captureEnabled: "Capture enabled",
    calendarJoin: "Join from my calendar",
    inArbi: "In Arbi",
    email: "Email",
    notificationsHeading: "Notifications",
    notifyColumn: "Notification",
    defaultOn: "Default on",
    locked: "Locked",
    defaultOnNamed: (label: string) => `Default on — ${label}`,
    lockedNamed: (label: string) => `Locked — ${label}`,
    inArbiNamed: (label: string) => `In Arbi — ${label}`,
    emailNamed: (label: string) => `Email — ${label}`,
    propertyCap: "You can add up to 50 properties.",
    propertyNameRequired: "Every property needs a name.",
    removeTag: (tag: string) => `Remove ${tag}`,
    moveUp: (name: string) => `Move ${name} up`,
    moveDown: (name: string) => `Move ${name} down`,
    range: (min: number, max: number) => `${min}–${max}`,
    about: (name: string) => `About ${name}`,
    changedKeys: (keys: string[]) => keys.join(", "),
    justification: "Justification",
    vocabulary: "Vocabulary",
    sharingCeiling: "Sharing ceiling",
    language: "Language",
    codeSwitch: "Code-switch set",
    consentDefault: "Consent default",
    consentCollection: "How consent is collected",
    consentCollectionEmail: "Email before the meeting",
    consentCollectionMeeting: "Only during the meeting",
    consentCollectionManual: "Interviewer collects it",
    consentCollectionDisabled: "Don't collect consent",
    audioPrompt: "Audio prompt policy",
    gracePeriod: "Grace period (seconds)",
    consentReminder: "Consent reminder (minutes)",
    noticeWindow: "Notice window (minutes)",
    noticeLead: "Pre-call notice lead (minutes before the meeting)",
    disclosureEditor: {
      title: "Disclosure paragraph",
      description: "The bot always posts the fixed sentence first; your paragraph follows it in the participant's language.",
      fixedSentence: "This meeting is being recorded and transcribed by Arbi for {bot_name}. Reply “arbi help” for options.",
      fixedSentenceNote: "{bot_name} is your organization's bot name from Bot design (or “Arbi Notetaker”).",
      language: (tag: string) => `Paragraph — ${tag}`,
      placeholder: "Why your organization records, who can see the recording, and how to opt out.",
    },
    erasure: {
      title: "Erase a person",
      description: "Removes the person's speech from every meeting in your organization, nulls their name and address on the meetings kept as proof, and leaves a withdrawal record. Meetings under legal hold wait for the hold to lift.",
      subjectKind: "Subject",
      profile: "Profile",
      email: "Email address",
      profileId: "Profile ID",
      profileIdHint: "The id from the profile page URL",
      emailField: "Email address",
      dsarTag: "DSAR export",
      erase: "Erase…",
      confirmTitle: "Erase this person?",
      confirmBody: "This cannot be undone. Their speech is cut from every meeting, their name becomes “Erased participant”, and notices to them stop. Recordings and transcripts of other participants are kept.",
      confirm: "Erase",
      erasing: "Requesting",
      requested: "Erasure requested.",
      dsar: "Export DSAR bundle",
      dsarReady: "The bundle is ready in a new tab. The link works for 15 minutes.",
      dsarBlocked: "Allow pop-ups for this site to open the bundle.",
      requests: "Requests",
      noRequests: "No erasure requests yet.",
      status: { pending: "Pending", running: "Running", done: "Done", failed: "Failed", blocked_legal_hold: "Waiting on a legal hold" },
      progress: (done: number, total: number) => `${done} of ${total} meeting${total === 1 ? "" : "s"}`,
      blocked: (n: number) => `${n} under legal hold`,
      requestedAt: (when: string) => `Requested ${when}`,
    },
    verifiedDomains: {
      title: "Verified email domains",
      description: "Domains your organization owns. Anyone with an email address on one of them counts as a colleague, whether or not they have an Arbi seat.",
      // The three things an admin gets wrong, per spec §3.2 / §3.4 / §3.6.
      exactMatch: "Exact match. Add mail.acme.com separately from acme.com.",
      whatItDoes: "Meetings where every attendee is on a verified domain, and nothing matched a job, are skipped.",
      // decision 229: there is no fan-out, so the delay is real and the override is the fast lever.
      whenItApplies: "Applies to meetings as they are re-checked, usually within a day. To stop a meeting sooner,",
      whenItAppliesLink: "skip it from the schedule",
      // r4's zero-code mitigation: doing this first makes the re-check window zero for the common case.
      beforeCalendars: "Add your domains before you connect a calendar. Domains added later apply to meetings already on the schedule only as they are re-checked.",
      addLabel: "Add a domain",
      addPlaceholder: "acme.com",
      add: "Add",
      adding: "Adding",
      remove: (domain: string) => `Remove ${domain}`,
      addedBy: (who: string, when: string) => `Added by ${who} · ${when}`,
      addedOn: (when: string) => `Added ${when}`,
      empty: "No verified domains yet. Until you add one, a meeting with a colleague who has no Arbi seat is treated as an external meeting.",
      added: "Domain added.",
      removed: "Domain removed.",
      count: (used: number, max: number) => `${used} of ${max}`,
    },
    /**
     * The org's one meeting-bot design (spec 10 §7, org-level). Anything that reaches a toast is
     * shaped for the shared component: `title` is a single TRUNCATING line inside a fixed 320 px
     * card (`components/ui/sonner.tsx:47,121`), the `description` below it wraps. So a headline
     * goes in `title` and every detail — dimensions, limits, the reason — goes in `description`
     * (§10 row 58). `bot-design-copy.test.ts` asserts the title ceiling. Page-body copy (the
     * helpers, the scope note) is NOT toast copy and stays a plain wrapping sentence.
     */
    botDesign: {
      description: "Every meeting bot in your organization uses this name and design.",

      nameLabel: "Bot name",
      namePlaceholder: "Arbi Notetaker",
      nameHelper:
        "Shown as the bot's participant name and on its video tile. Leave it empty to use “Arbi Notetaker”. Up to 60 characters.",

      tileLabel: "Video tile",
      tileHelper:
        "Optional artwork that replaces the whole tile. 16:9, at least 1280×720, PNG / JPEG / WebP up to 5 MB.",
      tileEmpty: "No video tile — the bot uses the Neuroscale-styled tile with the logo and bot name.",
      tileUpload: "Upload tile",
      tileReplace: "Replace tile",
      tileRemove: "Remove",
      cropTitle: "Crop video tile",

      logoLabel: "Logo",
      logoHelper:
        "Without a video tile, the logo sits in the center of the Neuroscale-styled tile, above the bot name. Square, at least 128×128, PNG / JPEG / WebP up to 5 MB. Transparency is kept.",
      logoEmpty: "No logo",
      logoUpload: "Upload logo",
      logoReplace: "Replace logo",
      logoRemove: "Remove",
      logoCropTitle: "Crop logo",

      previewCaption: "What participants see while recording",
      previewUnavailable: "Preview unavailable.",

      // §5.5 — the one case where white-label yields. There is no signal that it happened
      // (`signed_in_identity` has no writer; spec 06 decision 220), so this note is the whole mitigation.
      scopeSignedIn:
        "On meetings that require the bot to sign in, the platform shows the sign-in account's name instead.",

      saved: "Bot design updated",
      tileRemoved: "Tile removed",
      logoRemoved: "Logo removed",
      /**
       * §7 "Refresh notice" and §5.3's cutoff caveat, as one toast DESCRIPTION under the
       * `saved` / `tileRemoved` / `logoRemoved` title, which already says the save happened.
       *
       * §5.3 "Scope": a row whose bot joins inside REFRESH_CUTOFF_MIN keeps the old design BY
       * DESIGN. Deliberately not "10 minutes": the real bound is scheduled_start − join_lead_min
       * − 10, and join_lead_min is a per-org setting, so any number here would be wrong for some
       * org. "about to start" is true at every lead.
       */
      refreshNotice:
        "Bots already scheduled in your organization are being updated. Meetings that are about to start keep the previous design.",

      // Errors. Keyed by the server's `code`, never by its message (spec 04 §10.121).
      // Long ones are {title, description}: the sentence does not fit one truncating line.
      adminOnly: {
        title: "Not allowed",
        description: "Only an organization admin can change the bot design.",
      },
      nameInvalid: {
        title: "Bot name not saved",
        description: "A bot name has to be between 1 and 60 characters.",
      },
      // The server's dimensions ride in the description, where they wrap and stay readable.
      tooSmall: (width: number, height: number) => ({
        title: "That image is too small",
        description: `It's ${width}×${height}. The tile needs to be at least 1280×720.`,
      }),
      badAspect: (width: number, height: number) => ({
        title: "That image isn't 16:9",
        description: `It's ${width}×${height}. The tile has to be 16:9.`,
      }),
      invalidImage: {
        title: "Unsupported image",
        description: "The tile has to be a still PNG, JPEG or WebP image.",
      },
      logoTooSmall: (width: number, height: number) => ({
        title: "That logo is too small",
        description: `It's ${width}×${height}. The logo needs to be at least 128×128.`,
      }),
      logoBadAspect: (width: number, height: number) => ({
        title: "That logo isn't square",
        description: `It's ${width}×${height}. The logo has to be square.`,
      }),
      invalidLogo: {
        title: "Unsupported image",
        description: "The logo has to be a still PNG, JPEG or WebP image.",
      },
      // These already fit the title line, so they stay bare strings.
      tooLarge: "That file is over 5 MB.",
      logoTooLarge: "That logo is over 5 MB.",
      saveFailed: "Could not save the bot design.",
      tileSaveFailed: "Could not save the tile.",
      logoSaveFailed: "Could not save the logo.",
    },
    allowInterviewerOverride: "Allow interviewer consent override",
    ownDomainDeclines: "Own-domain declines",
    chatKnownUnavailable: "Chat known unavailable",
    regulatedRecording: "Regulated recording (days)",
    regulatedTranscript: "Regulated transcript (days)",
    businessDays: "Business (days)",
    mediaRegulated: "Regulated media",
    mediaBusiness: "Business media",
    exportTransfer: "Allow export transfer",
    participantCap: "Participant cap",
    joinLead: "Join lead (minutes)",
    lobbyWait: "Lobby wait (minutes)",
    memberWait: "Member wait (minutes)",
    emptyRoomWait: "Empty-room wait (minutes)",
    overrunAllowance: "Overrun allowance (minutes)",
    postWindowSilence: "Post-window silence (minutes)",
    maxSession: "Max session (hours)",
    panelHandoff: "Panel handoff (minutes)",
    minDuration: "Minimum duration (seconds)",
    notifySuppress: "Suppress notify under (seconds)",
    pausedReminder: "Paused reminder (minutes)",
    defaultAdhoc: "Default ad-hoc duration (minutes)",
    requireMember: "Require a member present",
    keepSecondary: "Keep secondary source",
    videoLayout: "Video layout",
    screenShare: "Screen-share content",
    schedulerAccess: "Scheduler default access",
    otherBot: "Other-bot policy",
    metricsExposure: "Interviewer metrics exposure",
    recipientPolicy: "Recipient policy",
    attachRecap: "Attach recap PDF",
    attachTranscript: "Attach transcript PDF",
    scorecardAutoGenerate: "Auto-generate scorecard",
    propertyName: "Name",
    propertyType: "Type",
    propertyOrder: "Order",
    noProperties: "No properties yet.",
    calendarMapping: "Calendar mapping",
    typeNamed: (name: string) => `Type — ${name}`,
    nameNamed: (name: string) => `Name — ${name}`,
    mappingNamed: (name: string) => `Calendar mapping — ${name}`,
    archiveNamed: (name: string) => `Archive — ${name}`,
    channels: {
      calendar: "Calendar",
      ats: "ATS",
      invite: "Invite",
      upload: "Upload",
      phone: "Phone",
      desktop: "Desktop",
    },
    listing: {
      calendar: "Calendar listings",
      ats: "ATS listings",
      invite: "Invite listings",
      adhoc: "Ad-hoc listings",
      upload: "Upload listings",
    },
    listingValues: { list: "List", entity_only: "Entity only" },
    notifications: {
      conv_ready: "Meeting ready",
      conv_scorecard_pending: "Scorecard pending",
      conv_unmatched: "Unmatched",
    },
    limits: {
      max_concurrent_bots: "Max concurrent bots",
      daily_llm_token_budget: "Daily LLM token budget",
      daily_asr_minutes: "Daily ASR minutes",
      daily_upload_bytes: "Daily upload bytes",
      max_calendars: "Max calendars",
      upload_presign_daily_user: "Upload presign daily (user)",
      upload_presign_daily_org: "Upload presign daily (org)",
      max_generators: "Max generators",
      daily_notice_cap: "Daily notice cap",
      max_stream_connections: "Max stream connections",
    },
    help: {
      consentDefault:
        "Whether people are recorded unless they opt out, or must opt in before recording starts.",
      consentCollection:
        "Email sends a request before the meeting. Only during the meeting asks after the bot joins and does not email. Interviewer collects it means the bot does not ask and assumes the interviewer will. Don't collect consent starts recording with no collection step and does not require the interviewer to ask.",
      gracePeriod:
        "How long the bot waits after joining for someone to object before it starts recording.",
      consentReminder:
        "How often the bot reminds people in the meeting that recording is on.",
      noticeWindow:
        "How late a meeting can start and still send the pre-call recording notice.",
      noticeLead:
        "How far ahead of the meeting people get an email that a recording bot will join.",
      disclosure:
        "Extra paragraph the bot posts in chat after the fixed recording notice, in each participant’s language.",
      audioPrompt:
        "When the bot speaks a consent prompt — first guest, every guest, or never.",
      allowInterviewerOverride:
        "Lets the interviewer change the consent rule for that one meeting.",
      ownDomainDeclines:
        "Treats people on your company email domain as already declined, so internal-only meetings are not recorded.",
      chatKnownUnavailable:
        "If the meeting platform has no chat, skip chat-based consent and use the spoken prompt instead.",
      regulatedRecording:
        "How long recordings from regulated meetings are kept before they are deleted.",
      regulatedTranscript:
        "How long transcripts from regulated meetings are kept before they are deleted.",
      businessDays:
        "How long ordinary meeting recordings and transcripts are kept before they are deleted.",
      justification:
        "Internal note explaining these retention windows. Participants never see this.",
      mediaRegulated:
        "Whether regulated meetings keep video, or audio only.",
      mediaBusiness:
        "Whether ordinary meetings keep video, or audio only.",
      exportTransfer:
        "Lets people download a meeting recording or transcript out of Arbi.",
      channels: {
        calendar: "Lets the bot join meetings from connected calendars.",
        ats: "Lets the bot join meetings booked through your ATS.",
        invite: "Lets people invite the bot to a meeting by email.",
        upload: "Lets people upload a recording they already captured.",
        phone: "Phone capture is not available yet.",
        desktop: "Desktop capture is not available yet.",
      },
      participantCap: "The bot leaves if more than this many people join.",
      joinLead:
        "How many minutes before the scheduled start the bot enters the meeting.",
      lobbyWait: "How long the bot waits in the lobby before giving up.",
      memberWait:
        "How long the bot waits for someone from your organization to join.",
      emptyRoomWait: "How long the bot stays if nobody else is in the room.",
      overrunAllowance:
        "Extra minutes the bot stays after the scheduled end time.",
      postWindowSilence:
        "After the meeting window, how long of silence before the bot leaves.",
      maxSession: "Hard cap on how long one bot session can last.",
      panelHandoff:
        "Extra minutes the bot stays so a panel interviewer can take over.",
      minDuration:
        "Meetings shorter than this are discarded and not shown as recordings.",
      notifySuppress:
        "Meetings shorter than this do not send “meeting ready” notifications.",
      pausedReminder:
        "How often the bot reminds the room that recording is paused.",
      defaultAdhoc:
        "Default length used when someone starts an ad-hoc recording.",
      requireMember:
        "The bot only records if someone from your organization is in the meeting.",
      keepSecondary:
        "When a desktop recording of the same meeting exists, keep it after the bot recording is saved. Off drops that desktop copy.",
      videoLayout: "How the recording frames people — speaker view or gallery.",
      screenShare: "Whose shared screen is included in the recording.",
      listing: {
        calendar:
          "Whether calendar meetings appear in the Meetings list, or only on the related job or candidate.",
        ats: "Whether ATS-booked meetings appear in the Meetings list, or only on the related job or candidate.",
        invite:
          "Whether invite-only meetings appear in the Meetings list, or only on the related job or candidate.",
        adhoc:
          "Whether ad-hoc recordings appear in the Meetings list, or only on the related job or candidate.",
        upload:
          "Whether uploaded recordings appear in the Meetings list, or only on the related job or candidate.",
      },
      schedulerAccess:
        "When a meeting is created from a connected calendar, the person who connected that calendar is granted access to the recording and transcript.",
      otherBot:
        "What this bot does if another recording bot is already in the meeting — leave, or stay.",
      sharingCeiling:
        "Highest access level anyone can share a meeting at. Set by Arbi.",
      metricsExposure:
        "Who can see named interviewer quality scores from meetings.",
      language:
        "Primary language the bot uses for speech, chat notices, and transcripts.",
      codeSwitch:
        "Extra languages the bot should recognize if people switch mid-meeting.",
      vocabulary:
        "Custom words the transcriber should prefer. Coming later.",
      recipientPolicy: "Who gets meeting-ready emails.",
      notifications: {
        conv_ready: "Notifies when a meeting’s recording and transcript are ready.",
        conv_scorecard_pending:
          "Notifies when a scorecard still needs to be filled in.",
        conv_unmatched: "Notifies when a meeting could not be matched to a job.",
      },
      attachRecap: "Attaches a recap PDF to the meeting-ready email.",
      attachTranscript: "Attaches a transcript PDF to the meeting-ready email.",
      scorecardAutoGenerate:
        "Starts a scorecard automatically when the meeting is ready.",
      data: "Custom fields stored on each meeting. People in the meeting do not see these.",
      erasure:
        "Removes that person’s speech from every meeting and stops notices to them. Other people’s recordings stay.",
      limits:
        "Daily and concurrent caps Arbi applies to this organization. Participants never see these.",
      captureEnabled:
        "When off, Arbi will not record meetings you host or join.",
      calendarJoin:
        "When on, the bot can join interviews on your connected calendar.",
    },
    preview: {
      mediaDueNow: "Recordings due now",
      transcriptsDueNow: "Transcripts due now",
      mediaDue30d: "Recordings due in 30 days",
      transcriptsDue30d: "Transcripts due in 30 days",
    },
    options: {
      opt_out: "Opt out",
      opt_in: "Opt in",
      first_external: "First external participant",
      each: "Each participant",
      off: "Off",
      speaker: "Speaker",
      gallery: "Gallery",
      audio_only: "Audio only",
      interviewer_only: "Interviewer only",
      candidate_only: "Candidate only",
      all: "All",
      auto_leave: "Auto-leave",
      stay: "Stay",
      aggregate_only: "Aggregate only",
      named_self_admins: "Named — self and admins",
      named_all: "Named — everyone",
      invited_members: "Invited members",
      project_members: "Project members",
      initiator: "Initiator",
      video_audio: "Video and audio",
      date: "Date",
      person: "Person",
      text: "Text",
      event_title: "Event title",
      attendee: "Attendee",
      org_only: "Organization only",
    },
  },
  share: {
    title: "Share this meeting",
    named: "A person",
    entity: "Everyone with access to a job or candidate",
    scope: { artifacts: "Documents only", media: "Recording and transcript only", both: "Everything" },
    entityRefused: "You also need manage-access on that job's project.",
    submit: "Share",
    cancel: "Cancel",
    person: "Person",
    job: "Job",
    candidate: "Candidate (optional)",
    scopeLabel: "Access",
    expiry: "Expires",
    kindLabel: "Who",
  },
  schedule: {
    columns: { when: "When", meeting: "Meeting", decision: "Bot", reason: "Why", override: "Override" },
    untitled: "Calendar meeting",
    attendees: (n: number) => `${n} attendee${n === 1 ? "" : "s"}`,
    counts: (members: number, external: number) => `${members} member${members === 1 ? "" : "s"} · ${external} external`,
    // spec 06 §6: a skip caused by a verified domain must be distinguishable from one caused by
    // seats. `external − external_for_skip` is how many attendees the domain arm reclassified.
    onVerifiedDomain: (n: number) => `${n} on a verified domain`,
    // The window starts at local midnight, so today's finished meetings are in range —
    // "Next N days" would be a lie on every row above the current time.
    window: (days: number) => `Today and the next ${days} days`,
    views: { list: "List", calendar: "Calendar" },
    filters: {
      label: "Show",
      upcoming: "Upcoming",
      cancelled: "Cancelled",
      skipped: "Skipped",
      all: "All",
    },
    viewGroup: "Schedule view",
    spans: { week: "Week", month: "Month" },
    spanGroup: "Calendar span",
    previousWeek: "Previous week",
    nextWeek: "Next week",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    moreMeetings: (n: number) => `+${n} more`,
    today: "Today",
    emptyHint: "Connect a calendar and the next 28 days of bots appear here.",
    truncated: "Showing the first 500 meetings in this window. There may be more that are not listed.",
    override: {
      action: "Bot",
      record: "Record",
      skip: "Skip",
      default: "Default",
      scope: "Apply to",
      occurrence: "This meeting",
      series: "Every occurrence",
      saved: "Saved",
      cannot: "This meeting can no longer be overridden — it already started, ended, or the bot could not join.",
      skipTitleSeries: "Skip every occurrence?",
      skipBodySeries:
        "This applies to every occurrence of the series, not a single meeting. Scheduled bots for those meetings will be cancelled. A bot that has already started joining may still join.",
      skipConfirm: "Skip",
    },
    openMeeting: "Open meeting",
  },
  calendars: {
    heading: "Calendars",
    description: "Connect a calendar so Arbi can send a bot to your interviews.",
    connectGoogle: "Connect Google Calendar",
    connectMicrosoft: "Connect Microsoft Outlook",
    syncDisclosure:
      "Connecting syncs your whole primary calendar to our meeting-bot provider for the next 28 days. A bot joins only the meetings your organization's join rules select.",
    provider: { google: "Google Calendar", microsoft: "Microsoft Outlook" },
    status: { connected: "Connected", needs_reconnect: "Reconnect needed", disconnected: "Disconnected" },
    reconnect: "Reconnect",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect this calendar?",
    disconnectBody:
      "Only scheduled meetings whose only calendar source is this connection will be cancelled.",
    disconnectKept:
      "Meetings already recorded stay. The connection is marked disconnected, not deleted.",
    disconnectPreview: (n: number) =>
      n === 0 ? "No scheduled meetings will be cancelled." : `${n} scheduled meeting${n === 1 ? "" : "s"} will be cancelled.`,
    disconnectConfirm: "Disconnect",
    disconnecting: "Disconnecting",
    connected: "Calendar connected.",
    reconnected: "Calendar reconnected.",       // 04d's reconnect callback redirects to ?calendar=reconnected
    disconnected: "Calendar disconnected.",
    connectFailed: "Couldn't connect the calendar.",
    none: "No calendars connected.",
    lastSync: (when: string) => `Synced ${when}`,
    neverSynced: "Not synced yet",
    syncing: "Syncing…",                        // rendered for the `pending:<recall id>` email_address sentinel (04d §10.62)
  },
} as const;
